import React from 'react';
import { useNavigate } from 'react-router-dom';
import Component from '../../components/Component';
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Typography,
  Chip,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import UpdateIcon from '@mui/icons-material/Update';
import SummarizeIcon from '@mui/icons-material/Summarize';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import QuizIcon from '@mui/icons-material/Quiz';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SettingsIcon from '@mui/icons-material/Settings';
import ShareIcon from '@mui/icons-material/Share';
import TranslateIcon from '@mui/icons-material/Translate';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SportsIcon from '@mui/icons-material/Sports';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SportsCricketIcon from '@mui/icons-material/SportsCricket';
import PsychologyIcon from '@mui/icons-material/Psychology';

const genAiCards = [
  {
    title: 'AI Chat (Duggy AI)',
    description: 'Conversational match analyst — answers fan questions about live and completed matches with real-time data.',
    icon: SmartToyIcon,
    link: '/admin/ai-chat',
    status: 'live',
    color: '#4CAF50',
    stats: '17 intents, streaming SSE, 3-variant cache',
  },
  {
    title: 'Hook Questions',
    description: 'Pre-match + post-match conversation hooks per match (H2H, matchups, pitch, MOTM verdict, etc.). Manually regenerate per match + phase.',
    icon: QuizIcon,
    link: '/admin/hook-questions',
    status: 'live',
    color: '#7B1FA2',
    stats: '14 scenarios · per-match + phase',
  },
  {
    title: 'Morning Brief (Daily Digest)',
    description: '10-card daily digest summarising yesterday\'s matches + today\'s schedule. Cron at 07:00 IST. Manually regenerate from here.',
    icon: WbSunnyIcon,
    link: '/admin/morning-brief',
    status: 'live',
    color: '#FFB300',
    stats: 'Daily 07:00 IST + manual trigger',
  },
  {
    title: 'Fan agents',
    description: 'Are @LoyalStump, @WhistlePodu, and @CricJudge working, what they already posted, and what they are about to post. The queue is a read-only preview of the live scorecard.',
    icon: SportsCricketIcon,
    link: '/admin/fan-agents',
    status: 'live',
    color: '#C62828',
    stats: 'Every 10 min · three agents',
  },
  {
    title: 'One-Line Match Updates',
    description: 'Auto-generated 40-char scroll-stopping one-liners for match tiles on homepage. Runs on cron for live matches.',
    icon: UpdateIcon,
    link: '/admin/match-one-liner',
    status: 'live',
    color: '#2196F3',
    stats: 'Every 15 min + milestone triggers',
  },
  {
    title: 'Match Summary (2–4 sentences)',
    description: 'Trigger-aware narrative summaries surfaced on the matchwall as ai_status_note. Fires on milestones (wicket / fifty / 100 / partnerships / big over / toss) and every interval.',
    icon: SummarizeIcon,
    link: '/admin/match-summary',
    status: 'live',
    color: '#0288D1',
    stats: 'Per match × trigger, full history',
  },
  {
    title: 'Feed Summaries',
    description: 'Topic-level summaries of fan posts (like NEWS in X). Generated per hashtag topic with poll options.',
    icon: SummarizeIcon,
    link: '/admin/feed-summary',
    status: 'live',
    color: '#FF9800',
    stats: 'Every +10 posts per topic',
  },
  {
    title: 'Player Moments',
    description: '10-word descriptors triggered by match events (centuries, wicket hauls, collapses). Dynamic cards on match wall.',
    icon: EmojiEventsIcon,
    link: '/player-moments',
    status: 'live',
    color: '#9C27B0',
    stats: '8 trigger events, weighted descriptors',
  },
  {
    title: 'Shareable Match Cards',
    description: 'Auto-generated match summary cards with top performers, key partnership, and result. Shareable on WhatsApp/Instagram.',
    icon: ShareIcon,
    link: '/admin/gen-ai/match-cards',
    status: 'new',
    color: '#E91E63',
    stats: 'API: GET /ai-chat/:matchId/card',
  },
  {
    title: 'Dynamic Prompts',
    description: 'Context-aware suggested questions that change based on live match state (collapse, milestone, phase change).',
    icon: AutoAwesomeIcon,
    link: '/admin/gen-ai/dynamic-prompts',
    status: 'new',
    color: '#00BCD4',
    stats: 'API: GET /ai-chat/:matchId/dynamic-prompts',
  },
  {
    title: 'Multi-Language Chat',
    description: 'AI Chat in Hindi (Hinglish), Tamil, Telugu, Kannada, Marathi, Bengali. Same data accuracy, regional voice.',
    icon: TranslateIcon,
    link: '/admin/gen-ai/languages',
    status: 'new',
    color: '#795548',
    stats: '7 languages supported',
  },
  {
    title: 'What-If Simulator',
    description: '"What if Kohli hadn\'t got out?" Projects alternate match outcomes using SR, remaining overs, and bowling quality.',
    icon: PsychologyIcon,
    link: '/admin/ai-chat',
    status: 'new',
    color: '#607D8B',
    stats: 'Intent: what_if',
  },
  {
    title: 'Fantasy Advisor',
    description: '"Should I captain Hazlewood?" AI recommends fantasy picks based on form, venue, matchups with confidence levels.',
    icon: TrendingUpIcon,
    link: '/admin/ai-chat',
    status: 'new',
    color: '#F44336',
    stats: 'Intent: fantasy_advisor',
  },
  {
    title: 'Cricket Explainer',
    description: '"What is DLS?" Explains cricket concepts using THIS match\'s data. Perfect for new/casual fans.',
    icon: SportsIcon,
    link: '/admin/ai-chat',
    status: 'new',
    color: '#3F51B5',
    stats: 'Intent: explain_cricket',
  },
  {
    title: 'Chat Credits',
    description: 'Manage user AI chat credits — view balances, add credits, set limits per user.',
    icon: CreditCardIcon,
    link: '/admin/ai-chat-credits',
    status: 'live',
    color: '#FF5722',
    stats: 'Fan energy → credits conversion',
  },
  {
    title: 'Gen AI Controls',
    description: 'Toggle Gen AI features on/off, configure LLM providers, set generation intervals and limits.',
    icon: SettingsIcon,
    link: '/admin/genai',
    status: 'live',
    color: '#9E9E9E',
    stats: 'Runtime config via admin panel',
  },
  {
    title: 'Token Usage Dashboard',
    description: 'Daily LLM token usage per use-case (one-liner, hook Q&A, player moments, feed summary, morning brief) with date-range picker. Use to track spend.',
    icon: TrendingUpIcon,
    link: '/admin/gen-ai/usage',
    status: 'new',
    color: '#00897B',
    stats: 'By usecase × day pivot',
  },
  {
    title: 'Per-Match Toggles',
    description: 'Tournament → match grid of on/off toggles for each GenAI use-case. Three-state per cell: ON / OFF / Default (follows global).',
    icon: SportsIcon,
    link: '/admin/gen-ai/match-controls',
    status: 'new',
    color: '#5E35B1',
    stats: 'Per match per usecase override',
  },
];

function StatusChip({ status }) {
  if (status === 'live') return <Chip label="LIVE" size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 700, fontSize: '0.7rem' }} />;
  if (status === 'new') return <Chip label="NEW" size="small" sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 700, fontSize: '0.7rem' }} />;
  return <Chip label={status.toUpperCase()} size="small" />;
}

function GenAiHub() {
  const navigate = useNavigate();

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <Box sx={{ py: 2 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" fontWeight={700}>Gen AI Hub</Typography>
              <Typography variant="body2" color="text.secondary">
                All AI-powered features across the Dugout platform. Manage, test, and monitor each use case.
              </Typography>
            </Box>

            <Grid container spacing={2.5}>
              {genAiCards.map((card, idx) => {
                const Icon = card.icon;
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                    <Card
                      elevation={1}
                      sx={{
                        height: '100%',
                        borderTop: `3px solid ${card.color}`,
                        transition: 'box-shadow 0.2s, transform 0.2s',
                        '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
                      }}
                    >
                      <CardActionArea
                        onClick={() => navigate(card.link)}
                        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 0 }}
                      >
                        <CardContent sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Icon sx={{ color: card.color, fontSize: 28 }} />
                              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                                {card.title}
                              </Typography>
                            </Box>
                            <StatusChip status={card.status} />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 48 }}>
                            {card.description}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                            {card.stats}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </div>
      </div>
    </Component>
  );
}

export default GenAiHub;
